import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ScrollView, View } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Text, Icon } from '@components/base'
import { AllCoachProfilesQuery } from '@src/graphql/fetchAllCoachProfiles.generated'
import { CoachCard } from '@src/screens/NutritionistHub/components/NutritionistSwitch/CoachCard'
import { EhrCoachSpecialty, UiInlineAlert } from '@src/types'
import { InlineAlert } from '@src/components'

const DEFAULT_PAGE = 1

const defaultSelectedSpecialties: EhrCoachSpecialty[] = []

interface ChooseCoachProps {
  title: string
  loadMoreButtonText?: string
  onLoadMore?: () => void
  perPage: number
  selectedSpecialties?: EhrCoachSpecialty[]
  loadCoachProfiles: ({
    page,
    specialties,
  }: {
    page: number
    specialties: EhrCoachSpecialty[]
  }) => Promise<AllCoachProfilesQuery['allCoachProfiles']>
  onNoCoachesFound?: () => void
  onCoachSelect: (
    coachProfile: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0],
  ) => void
  generateChooseCoachButtonText?: (
    coachProfile: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0],
  ) => string
  showFilters?: boolean
  alertMessage?: string
  onShowFilters?: () => void
}

export const ChooseCoach = (props: ChooseCoachProps) => {
  const selectedSpecialties = props.selectedSpecialties ?? defaultSelectedSpecialties
  const selectedSpecialtiesKey = [...selectedSpecialties].sort().join('|')

  return (
    <ChooseCoachContent
      key={selectedSpecialtiesKey}
      {...props}
      selectedSpecialties={selectedSpecialties}
    />
  )
}

const ChooseCoachContent = ({
  title,
  loadMoreButtonText = 'Load More',
  onLoadMore,
  selectedSpecialties = defaultSelectedSpecialties,
  loadCoachProfiles: loadCoachProfilesFromProps,
  perPage,
  onNoCoachesFound,
  onCoachSelect,
  generateChooseCoachButtonText,
  showFilters = true,
  alertMessage,
  onShowFilters,
}: ChooseCoachProps) => {
  const styles = useStyleSheet(themedStyles)
  const insets = useSafeAreaInsets()

  const [coachProfiles, setCoachProfiles] = useState<
    AllCoachProfilesQuery['allCoachProfiles']['coachProfiles']
  >([])

  const [page, setPage] = useState(DEFAULT_PAGE)

  const [hasMoreData, setHasMoreData] = useState(true)

  const loadedInitialDataRef = useRef(false)

  const loadCoachProfiles = useCallback(
    ({ page, specialties }: { page: number; specialties: EhrCoachSpecialty[] }) => {
      loadCoachProfilesFromProps({ page, specialties }).then((response) => {
        setCoachProfiles((prev) => [...prev, ...response.coachProfiles])
        if (response.coachProfiles.length < perPage) {
          setHasMoreData(false)
        }
        if (page === DEFAULT_PAGE && response.coachProfiles.length === 0) {
          onNoCoachesFound?.()
        }
      })
    },
    [loadCoachProfilesFromProps, onNoCoachesFound, perPage],
  )

  useEffect(() => {
    if (loadedInitialDataRef.current) {
      return
    }

    loadedInitialDataRef.current = true

    loadCoachProfiles({ page: DEFAULT_PAGE, specialties: selectedSpecialties })
  }, [loadCoachProfiles, selectedSpecialties])

  const handleLoadMore = () => {
    const nextPage = page + 1

    setPage(nextPage)
    loadCoachProfiles({ page: nextPage, specialties: selectedSpecialties })
    onLoadMore?.()
  }

  return (
    <ScrollView contentContainerStyle={[styles.container, { paddingBottom: insets.bottom + 16 }]}>
      <View style={styles.header}>
        <Text type="regular" style={styles.title}>
          {title}
        </Text>
        {showFilters && onShowFilters && (
          <Button
            type="outline"
            accessibilityLabel="Filters"
            size="small"
            onPress={onShowFilters}
            accessoryRight={<Icon name="funnel" />}
          >
            Filters
          </Button>
        )}
      </View>
      {alertMessage && (
        <InlineAlert style={styles.alert} category={UiInlineAlert.Neutral} message={alertMessage} />
      )}
      <View style={styles.cardList}>
        {coachProfiles.map((coachProfile) => (
          <CoachCard
            key={coachProfile.id}
            coachProfile={coachProfile}
            onCoachSelect={onCoachSelect}
            generateButtonText={generateChooseCoachButtonText}
          />
        ))}
      </View>
      {hasMoreData && (
        <Button
          type="outline"
          size="block"
          onPress={handleLoadMore}
          accessibilityLabel={loadMoreButtonText}
          style={styles.loadMoreButton}
        >
          {loadMoreButtonText}
        </Button>
      )}
    </ScrollView>
  )
}

const themedStyles = StyleService.create({
  container: {
    flexGrow: 1,
    backgroundColor: 'theme.background',
    paddingTop: 16,
    marginHorizontal: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 4,
    marginBottom: 16,
  },
  title: {
    fontSize: 17,
    fontWeight: '700',
  },
  alert: {
    marginBottom: 16,
  },
  cardList: {
    gap: 12,
  },
  loadMoreButton: {
    marginTop: 24,
  },
})
